import { useCallback, useEffect, useState } from 'react';
import { useEditorStore } from '@/store/editorStore';
import { connectionsApi } from '@/api/connections';
import type { Connection, DatabaseObjectInfo, SqlObjectType } from '@/types/domain';
import { ConnectionModal } from './ConnectionModal';
import { TableDesignModal } from './TableDesignModal';
import { ViewDefinitionModal } from './ViewDefinitionModal';
import './Sidebar.css';

// ── Agrupamento de conexões por servidor ──────────────────────────────────────

interface ServerGroup {
  name: string;
  engine: 'mssql' | 'pg';
  badge: 'PROD' | 'HML' | 'DEV';
  databases: Connection[];
}

function groupByServer(connections: Connection[]): ServerGroup[] {
  const map = new Map<string, ServerGroup>();
  for (const c of connections) {
    const key = c.name;
    if (!map.has(key)) {
      map.set(key, {
        name: c.name,
        engine: c.engine === 'MsSql' ? 'mssql' : 'pg',
        badge: envBadge(c.environment),
        databases: [],
      });
    }
    map.get(key)!.databases.push(c);
  }
  return Array.from(map.values());
}

function envBadge(env: string): 'PROD' | 'HML' | 'DEV' {
  if (env === 'Production') return 'PROD';
  if (env === 'Homologation') return 'HML';
  return 'DEV';
}

// ── Ícones e helpers ──────────────────────────────────────────────────────────

const OBJ_ICON: Record<SqlObjectType, string> = {
  Table: '▤', View: '◫', Procedure: 'Ρ', Function: 'ƒ', Index: '◈', Trigger: '⚡',
};

const OBJ_LABEL: Record<SqlObjectType, string> = {
  Table: 'Tables', View: 'Views', Procedure: 'Procedures',
  Function: 'Functions', Index: 'Indexes', Trigger: 'Triggers',
};

// ── Componente de objetos (lazy-loaded) ───────────────────────────────────────

type DesignTarget = { connectionId: string; schema: string; table: string };
type ViewTarget = { connectionId: string; schema: string; view: string };

function DbObjects({ connectionId, onDesign, onViewDef }: {
  connectionId: string;
  onDesign: (t: DesignTarget) => void;
  onViewDef: (t: ViewTarget) => void;
}) {
  const [objects, setObjects] = useState<DatabaseObjectInfo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setLoadError(null);
    connectionsApi.listObjects(connectionId)
      .then(data => { setObjects(data); })
      .catch(err => {
        setObjects([]);
        const msg = err?.response?.data?.error ?? err?.message ?? 'Erro ao carregar objetos';
        setLoadError(msg);
      });
  }, [connectionId]);

  if (objects === null) return (
    <div className="tree-node tree-depth-2">
      <span className="tree-caret-leaf" />
      <span className="tree-label" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>carregando...</span>
    </div>
  );

  if (loadError) return (
    <div className="tree-node tree-depth-2" style={{ height: 'auto', alignItems: 'flex-start', paddingTop: 4, paddingBottom: 4 }}>
      <span className="tree-caret-leaf" style={{ marginTop: 2 }} />
      <span className="tree-label" style={{ color: 'var(--prod)', fontSize: 11, whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {loadError}
      </span>
    </div>
  );

  if (objects.length === 0) return null;

  // Agrupa por tipo
  const byType = new Map<SqlObjectType, DatabaseObjectInfo[]>();
  for (const o of objects) {
    if (!byType.has(o.objectType)) byType.set(o.objectType, []);
    byType.get(o.objectType)!.push(o);
  }

  return <>
    {Array.from(byType.entries()).map(([type, items]) => (
      <ObjectTypeGroup key={type} type={type} items={items} connectionId={connectionId} onDesign={onDesign} onViewDef={onViewDef} />
    ))}
  </>;
}

function ObjectTypeGroup({
  type, items, connectionId, onDesign, onViewDef,
}: {
  type: SqlObjectType;
  items: DatabaseObjectInfo[];
  connectionId: string;
  onDesign: (t: DesignTarget) => void;
  onViewDef: (t: ViewTarget) => void;
}) {
  const [open, setOpen] = useState(false);
  return <>
    <div className="tree-node tree-depth-2 group" onClick={() => setOpen(o => !o)}>
      <span className="tree-caret">{open ? '▾' : '▸'}</span>
      <span className="tree-icon">{OBJ_ICON[type]}</span>
      <span className="tree-label">{OBJ_LABEL[type]} <span style={{ color: 'var(--text-muted)' }}>({items.length})</span></span>
    </div>
    {open && items.map(obj => (
      <div
        key={`${obj.schema}.${obj.name}`}
        className="tree-node tree-depth-3"
        draggable
        onDragStart={e => {
          e.dataTransfer.setData('text/plain', `${obj.schema}.${obj.name}`);
          e.dataTransfer.effectAllowed = 'copy';
        }}
      >
        <span className="tree-drag-handle" title="Arrastar para o editor">⠿</span>
        <span className={`tree-icon ${type.toLowerCase()}`}>{OBJ_ICON[type]}</span>
        <span className="tree-label">{obj.schema}.{obj.name}</span>
        {type === 'Table' && (
          <span
            className="tree-design-btn"
            title="Ver design da tabela"
            onClick={e => { e.stopPropagation(); onDesign({ connectionId, schema: obj.schema, table: obj.name }); }}
          >cols</span>
        )}
        {type === 'View' && (
          <span
            className="tree-design-btn"
            title="Ver definição da view"
            onClick={e => { e.stopPropagation(); onViewDef({ connectionId, schema: obj.schema, view: obj.name }); }}
          >def</span>
        )}
      </div>
    ))}
  </>;
}

// ── Componente principal ──────────────────────────────────────────────────────

export function Sidebar() {
  const connections = useEditorStore(s => s.connections);
  const connectionsLoading = useEditorStore(s => s.connectionsLoading);
  const connectionsError = useEditorStore(s => s.connectionsError);
  const selectedConnectionId = useEditorStore(s => s.selectedConnectionId);
  const loadConnections = useEditorStore(s => s.loadConnections);
  const selectConnection = useEditorStore(s => s.selectConnection);

  const [openServers, setOpenServers] = useState<Set<string>>(new Set());
  const [openDbs, setOpenDbs] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConnection, setEditingConnection] = useState<Connection | undefined>(undefined);
  const [designTarget, setDesignTarget] = useState<DesignTarget | null>(null);
  const [viewTarget, setViewTarget]     = useState<ViewTarget | null>(null);

  const handleDesign   = useCallback((t: DesignTarget) => setDesignTarget(t), []);
  const handleViewDef  = useCallback((t: ViewTarget) => setViewTarget(t), []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const servers = groupByServer(connections);
  const filterLower = filter.toLowerCase();
  const filteredServers = filterLower
    ? servers.map(s => ({
        ...s,
        databases: s.databases.filter(
          db => db.database.toLowerCase().includes(filterLower) ||
                db.name.toLowerCase().includes(filterLower)
        ),
      })).filter(s => s.databases.length > 0)
    : servers;

  const toggleServer = (name: string) =>
    setOpenServers(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });

  const toggleDb = (id: string) =>
    setOpenDbs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <>
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-title">Conexões</div>
        <span className="add-btn" title="Adicionar conexão" onClick={() => setShowAddModal(true)}>+</span>
      </div>

      <input
        className="sidebar-search"
        placeholder="Filtrar..."
        value={filter}
        onChange={e => setFilter(e.target.value)}
      />

      <div className="tree">
        {connectionsLoading && (
          <div className="tree-node">
            <span className="tree-label" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Carregando conexões...
            </span>
          </div>
        )}

        {connectionsError && (
          <div className="tree-node" style={{ color: 'var(--prod)' }}>
            <span className="tree-label">{connectionsError}</span>
          </div>
        )}

        {filteredServers.map(server => {
          const isOpen = openServers.has(server.name);
          return (
            <div key={server.name}>
              <div className="tree-node server" onClick={() => toggleServer(server.name)}>
                <span className="tree-caret">{isOpen ? '▾' : '▸'}</span>
                <span className={`tree-icon ${server.engine}`}>
                  {server.engine === 'mssql' ? 'MS' : 'PG'}
                </span>
                <span className="tree-label">{server.name}</span>
                <span className={`tree-badge ${server.badge.toLowerCase()}`}>{server.badge}</span>
              </div>

              {isOpen && server.databases.map(db => {
                const isDbOpen = openDbs.has(db.id);
                const isSelected = selectedConnectionId === db.id;
                return (
                  <div key={db.id}>
                    <div
                      className={`tree-node tree-depth-1 db ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        selectConnection(db.id);
                        toggleDb(db.id);
                      }}
                    >
                      <span className="tree-caret">{isDbOpen ? '▾' : '▸'}</span>
                      <span className="tree-icon db">▣</span>
                      <span className="tree-label">{db.database}</span>
                      {db.readOnly && <span className="tree-badge pii">RO</span>}
                      <span
                        className="tree-edit-btn"
                        title="Editar conexão"
                        onClick={e => { e.stopPropagation(); setEditingConnection(db); }}
                      >&#9998;</span>
                    </div>

                    {isDbOpen && <DbObjects connectionId={db.id} onDesign={handleDesign} onViewDef={handleViewDef} />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </aside>
    {showAddModal && <ConnectionModal onClose={() => setShowAddModal(false)} />}
    {editingConnection && (
      <ConnectionModal
        editConnection={editingConnection}
        onClose={() => setEditingConnection(undefined)}
      />
    )}
    {designTarget && (
      <TableDesignModal
        connectionId={designTarget.connectionId}
        schema={designTarget.schema}
        table={designTarget.table}
        onClose={() => setDesignTarget(null)}
      />
    )}
    {viewTarget && (
      <ViewDefinitionModal
        connectionId={viewTarget.connectionId}
        schema={viewTarget.schema}
        view={viewTarget.view}
        onClose={() => setViewTarget(null)}
      />
    )}
    </>
  );
}
